import {
	API_ENDPOINTS,
	DataTable,
	DateFormats,
	formatDateFns,
	FormInput,
	FormInputType,
	Icon,
	Icons,
	customToast,
	Modal,
	PageLayout,
	Permissions,
	replaceKeyWithValue,
	Roles,
	route,
	StringHelpers,
	useAuthorisationContext,
	useLangContext,
	useQueries,
	Variants,
	DataColumnInterface,
} from 'carrier-fe';
import { useMemo, useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AdminClaimListItem } from '../../types/claims';
import { CompanyTypes, SEARCH_FILTERS } from '../../Constants';
import exportClaims from './helpers/export-claims';

function ClaimsList() {
	// Hooks and Contexts
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { crud, fields, responses } = useLangContext();
	const { hasPermissionAccess, hasRoleAccess } = useAuthorisationContext();

	// State Management
	const [refreshOnChange, setRefreshOnChange] = useState(Date.now());
	const [selected, setSelected] = useState<string[]>([]);
	const [exportModalOpen, setExportModalOpen] = useState(false);
	const [exporting, setExporting] = useState<boolean>(false);

	// Queries
	const { data: selectData } = useQueries([
		{
			key: 'distributors',
			config: {
				method: 'GET',
				url: route(
					API_ENDPOINTS.GENERIC.SELECT.COMPANIES +
						`?types[]=${CompanyTypes.DISTRIBUTOR}`
				),
			},
		},
		{
			key: 'companies',
			config: {
				method: 'GET',
				url: route(
					API_ENDPOINTS.GENERIC.SELECT.COMPANIES +
						`?types[]=${CompanyTypes.INSTALLER}`
				),
			},
		},
	]);

	// Derived States
	const distributorOptions = useMemo(
		() => selectData?.distributors?.data ?? [],
		[selectData]
	);

	const companyOptions = useMemo(
		() => selectData?.companies?.data ?? [],
		[selectData]
	);

	const title = useMemo(
		() =>
			StringHelpers.title(
				replaceKeyWithValue(
					crud?.pages?.index?.title || ':model List',
					'model',
					crud?.models?.claim || 'Claim'
				)
			),
		[crud]
	);

	const filters: FormInputType[] = useMemo(() => {
		const filterList: FormInputType[] = [];

		if (hasRoleAccess(Roles.PLATFORM_MANAGER)) {
			filterList.push({
				name: 'distributor_id',
				label: StringHelpers.title(
					fields?.distributor || 'Distributor'
				),
				type: 'searchable-select',
				value: '',
				placeholder: StringHelpers.title(
					fields?.please_select || 'Please Select'
				),
				options: distributorOptions,
			});
		}

		filterList.push({
			name: 'company_id',
			label: StringHelpers.title(
				fields?.company || 'Company'
			),
			type: 'searchable-select',
			value: '',
			placeholder: StringHelpers.title(
				fields?.please_select || 'Please Select'
			),
			options: companyOptions,
		});

		return filterList;
	}, [distributorOptions, companyOptions, fields]);

	// Effects
	useEffect(() => {
		if (exporting) {
			handleExport();
		}
	}, [exporting]);

	// Handler functions
	const handleOptionSelect = (action: any, data: AdminClaimListItem) => {
		const { id = '' } = data;

		const actions: { [key: string]: () => void } = {
			view: () => {
				const searchFilter = searchParams.get('filter[search]');
				if (searchFilter) {
					sessionStorage.setItem(
						SEARCH_FILTERS.CLAIM,
						searchFilter
					);
				}
				navigate(`/claims/${id}`);
			},
		};

		if (typeof action === 'string' && actions[action]) {
			actions[action]();
		}
	};

	const handleExport = async () => {
		try {
			await exportClaims({
				claimIds: selected,
				fallbackFilename: `${new Date().toISOString().split('T')[0]}-bulk-claims-export.csv`,
				successMessage: replaceKeyWithValue(
					responses?.export,
					'attribute',
					StringHelpers.lower(
						crud?.models?.claim
						|| 'claim'
					)
				),
			});
			setRefreshOnChange(Date.now());
		} catch (error: any) {
			customToast({
				title:
					error?.response?.data?.message ||
					'An error occurred. Please try again.',
				variant: Variants.Danger,
			});
		} finally {
			setExporting(false);
			setExportModalOpen(false);
		}
	};

	const dropdownItems = useMemo(() => {
		const items = [];

		if (hasPermissionAccess(Permissions.TOSHIBA_ADMIN_CLAIM_BULK_EXPORT)) {
			items.push({
				label: StringHelpers.title(
					replaceKeyWithValue(
						crud?.buttons?.export?.dynamic?.default ||
							'Export :any',
						'any',
						crud?.models?.claim || 'Claims'
					)
				),
				disabled: false,
				onClick: () => {
					if (selected.length === 0) {
						customToast({
							title:
								crud?.buttons?.export
									?.select_companies_to_export ||
								'Please select items to export.',
							variant: Variants.Warning,
						});
						setExportModalOpen(false);
						return;
					}
					setExportModalOpen(true);
				},
				icon: <Icon icon={Icons.DOWNLOAD} />,
			});
		}

		return items;
	}, [crud, hasPermissionAccess, selected]);

	// Column mapping
	const colMapping = (
		data: AdminClaimListItem
	): DataColumnInterface[] => {
		const addressDisplay =
			[data?.address_line_1, data?.town_city, data?.postcode_zipcode]
				.filter(Boolean)
				.join(', ') || '-';

		return [
			{
				id: 'distributor_name',
				label: StringHelpers.title(
					fields?.distributor || 'Distributor'
				),
				value: data?.distributor_name || '-',
				sortable: false,
				visibleCondition: () => hasRoleAccess(Roles.PLATFORM_MANAGER),
			},
			{
				id: 'customer_name',
				label: StringHelpers.title(
					fields?.customer_name || 'Customer'
				),
				value: data?.customer_name || '-',
				sortable: true,
			},
			{
				id: 'address',
				label: StringHelpers.title(
					fields?.address || 'Address'
				),
				value: addressDisplay,
				sortable: false,
			},
			{
				id: 'created_at',
				label: StringHelpers.title(
					fields?.created_at || 'Claim Date'
				),
				value:
					formatDateFns(
						data?.created_at,
						DateFormats.DATETIME_SHORT
					) || '-',
				sortable: true,
			},
			{
				id: 'status',
				label: StringHelpers.title(
					fields?.status || 'Status'
				),
				value: data?.status_display || '-',
			},
			{
				id: 'deleted_at',
				label: StringHelpers.title(
					fields?.archived_at || 'Archived'
				),
				value:
					formatDateFns(
						data?.deleted_at,
						DateFormats.DATETIME_SHORT
					) || '-',
				sortable: true,
				visibleCondition: () => !!data.deleted_at,
			},
			{
				id: 'actions',
				label: StringHelpers.title(
					fields?.actions || 'Actions'
				),
				value: data.permissions?.view ? (
					<FormInput
						type="select"
						label={StringHelpers.title(
							fields?.actions || 'Action'
						)}
						placeholder={StringHelpers.title(
							fields?.select || 'Select'
						)}
						name="actions"
						value=""
						onChange={(value) => {
							if (!!value && typeof value === 'string') {
								handleOptionSelect(value, data);
							}
						}}
						options={[
							{
								label: StringHelpers.title(
									crud?.view || 'View'
								),
								value: 'view',
							},
						]}
						noMargin
					/>
				) : null,
			},
		];
	};

	return (
		<PageLayout title={title}>
			<DataTable<AdminClaimListItem>
				key={
					searchParams.get('filter[trashed]')
						? 'archived-view'
						: 'default-view'
				}
				routeEndpoint={route(API_ENDPOINTS.ADMIN.CLAIM.INDEX)}
				colMapping={colMapping}
				items={dropdownItems}
				incrementPaginationButtons
				refreshOnChange={String(refreshOnChange)}
				onSelect={(selected) => {
					const selectedIds = (selected as (string | number)[])
						.map((item) => item?.toString())
						.filter(
							(id): id is string => id !== undefined
						);
					setSelected(selectedIds);
				}}
				selectable
				variant={Variants.Info}
				filters={filters}
			/>
			<Modal
				open={exportModalOpen}
				onClose={() => setExportModalOpen(false)}
				title={StringHelpers.title(
					replaceKeyWithValue(
						crud?.buttons?.export?.dynamic?.default ||
							'Export :any',
						'any',
						crud?.models?.claim || 'Claims'
					)
				)}
				confirmText={
					exporting
						? crud?.buttons?.export?.submitting
						: crud?.buttons?.export?.default
				}
				closeText={
					crud?.buttons?.cancel?.default || 'Cancel'
				}
				onConfirm={() => setExporting(true)}
				variant={Variants.Danger}
			>
				{crud?.modals?.export?.description?.map(
					(item: string, key: number) => {
						return (
							<p key={key}>
								{replaceKeyWithValue(
									item,
									'name',
									StringHelpers.lower(
										crud?.models?.claim 
										|| 'Claims'
									)
								)}
							</p>
						);
					}
				)}
				<div className="d-flex flex-column mb-3">
					<p>
						<strong>
							{StringHelpers.title(
								crud?.buttons?.export
									?.selected_claims ||
									'Selected Claims'
							)}
							:{' '}
						</strong>
						{selected.length}
					</p>
				</div>
			</Modal>
		</PageLayout>
	);
}

export default ClaimsList;
